<template>
  <div class="agreement-preview" :class="{ printable: printable }">
    <header class="agreement-head">
      <h1>Holiday Schedule</h1>
      <p class="agreement-parties">
        Between <strong>{{ partyAName || 'Party A' }}</strong> and <strong>{{ partyBName || 'Party B' }}</strong>
      </p>
      <p class="agreement-meta">
        <span>Generated {{ today }}</span>
        <span v-if="jurisdiction"> · {{ jurisdictionLabel }}</span>
      </p>
      <p class="agreement-intro">
        The parties agree that {{ childrenIntro }} will spend time with each parent on
        the holidays, special days, and school breaks listed below, according to the
        arrangements set out for each occasion.
      </p>
      <p v-if="discussionCount > 0" class="agreement-discuss-banner">
        <strong>{{ discussionCount }} {{ discussionCount === 1 ? 'item is' : 'items are' }} still under discussion.</strong>
        Marked items below are not yet finalized and will need to be agreed before this
        schedule is signed.
      </p>
    </header>

    <section v-for="cat in groupedClauses" :key="cat.key" class="agreement-section">
      <h2>{{ cat.label }}</h2>
      <ol class="clauses">
        <li v-for="c in cat.clauses" :key="c.id">
          {{ c.prose }}<span v-if="c.flagged" class="discuss-tag"> (under discussion)</span>
        </li>
      </ol>
    </section>

    <p v-if="!groupedClauses.length" class="empty-state">
      No arrangements decided yet. Configure holidays on the left to populate the agreement.
    </p>
  </div>
</template>

<script>
import { renderClause } from '../proseRenderer.js'
import { CATEGORIES } from '../seed/holidays.js'

const CATEGORY_ORDER = ['holiday', 'special-day', 'school-break', 'christmas']
const UNDECIDED_KINDS = new Set(['unset', 'agreement-required'])
function isUndecided(h) {
  return UNDECIDED_KINDS.has(h?.rule?.kind)
}
const PROVINCE_NAMES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba',
  NB: 'New Brunswick', NL: 'Newfoundland & Labrador', NS: 'Nova Scotia',
  NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario',
  PE: 'Prince Edward Island', SK: 'Saskatchewan', YT: 'Yukon',
}

export default {
  name: 'AgreementPreview',
  components: {},
  props: {
    holidays: { type: Array, required: true },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    jurisdiction: { type: String, default: '' },
    children: { type: Array, default: () => [] },
    printable: { type: Boolean, default: false },
  },
  computed: {
    today() {
      return new Date().toLocaleDateString('en-CA', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    },
    jurisdictionLabel() {
      return PROVINCE_NAMES[this.jurisdiction] || this.jurisdiction
    },
    childrenIntro() {
      if (Array.isArray(this.children) && this.children.length === 1) {
        return this.children[0]?.name || 'their child'
      }
      if (Array.isArray(this.children) && this.children.length >= 2) {
        return 'the Children'
      }
      return 'their children'
    },
    groupedClauses() {
      const parties = {
        partyAName: this.partyAName,
        partyBName: this.partyBName,
        children: this.children,
      }
      const byCategory = {}
      for (const h of this.holidays) {
        // Skip undecided rows — they belong in the editor, not the agreement.
        if (isUndecided(h)) continue
        const list = (byCategory[h.category] ||= [])
        list.push({
          id: h.id,
          prose: renderClause(h, parties),
          flagged: !!h.flaggedForDiscussion,
        })
      }
      return CATEGORY_ORDER
        .filter((key) => byCategory[key]?.length)
        .map((key) => ({ key, label: CATEGORIES[key], clauses: byCategory[key] }))
    },
    // Count of clauses (i.e. decided rows) that are also flagged for discussion.
    // Drives the banner at the top of the preview / printed PDF.
    discussionCount() {
      let n = 0
      for (const cat of this.groupedClauses) {
        for (const c of cat.clauses) if (c.flagged) n++
      }
      return n
    },
  },
}
</script>

<style scoped>
.agreement-preview {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.75rem 1.85rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #1e293b;
}

.agreement-head h1 {
  margin: 0 0 0.4rem;
  font-size: 1.4rem;
  font-weight: 700;
  color: #34555f;
  letter-spacing: -0.01em;
  border-left: 3px solid #f7a91b;
  padding-left: 0.6rem;
}
.agreement-parties {
  margin: 0 0 0.2rem;
  font-size: 0.95rem;
}
.agreement-meta {
  margin: 0 0 1rem;
  font-size: 0.78rem;
  color: #64748b;
  display: flex;
  gap: 0.4rem;
}
.agreement-intro {
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: #475569;
  border-left: 3px solid #a3bbc1;
  padding: 0.4rem 0.85rem;
  background: #f8fafc;
  border-radius: 0 6px 6px 0;
}

/* Banner shown when at least one decided clause is flagged for discussion.
   Honest signal on a printed draft taken to mediation: this isn't final yet. */
.agreement-discuss-banner {
  margin: 0 0 1.25rem;
  font-size: 0.85rem;
  color: #85581a;
  border-left: 3px solid #f7a91b;
  padding: 0.5rem 0.85rem;
  background: #fef6e0;
  border-radius: 0 6px 6px 0;
}
.agreement-discuss-banner strong { color: #85581a; }

/* Inline tag appended to a clause that's flagged for discussion. */
.discuss-tag {
  font-style: italic;
  color: #85581a;
  font-size: 0.85em;
  font-weight: 500;
}

.empty-state {
  margin: 1.5rem 0 0.5rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
  font-style: italic;
}

.agreement-section { margin-top: 1.25rem; }
.agreement-section h2 {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #3f6772;
  font-weight: 700;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.35rem;
}
.clauses {
  margin: 0;
  padding-left: 1.4rem;
}
.clauses li {
  margin-bottom: 0.5rem;
}

/* Print: render only the agreement (the right-side preview) on 8.5×11 letter.
   Keeps the rendered prose identical to the live side panel — no editor controls,
   no buttons, no rotation table. Hooks live in HolidayBuilder.vue's print rules. */
@media print {
  .agreement-preview {
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    overflow: visible;
    /* Slightly smaller body font + tighter line spacing so 24 clauses
       compress comfortably onto fewer pages without feeling cramped. */
    font-size: 0.8rem;
    line-height: 1.35;
  }

  /* Pagination strategy:
     - Sections (Statutory, Special, etc.) CAN break across pages — long
       sections shouldn't get pushed wholesale to page 2.
     - Section headings stick with the first clause underneath them
       (no orphan headings at the bottom of a page).
     - Individual clauses never split mid-sentence across pages. */
  .agreement-section h2 {
    page-break-after: avoid;
    break-after: avoid;
  }
  .clauses li {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Tighter header spacing — the title block doesn't need the same
     breathing room as the on-screen preview. */
  .agreement-head h1 { font-size: 1.15rem; margin-bottom: 0.25rem; }
  .agreement-parties { font-size: 0.85rem; margin-bottom: 0.1rem; }
  .agreement-meta { font-size: 0.7rem; margin-bottom: 0.55rem; }
  .agreement-intro {
    font-size: 0.78rem;
    line-height: 1.35;
    margin-bottom: 0.7rem;
    padding: 0.3rem 0.7rem;
  }
  .agreement-discuss-banner {
    font-size: 0.78rem;
    line-height: 1.35;
    margin-bottom: 0.7rem;
    padding: 0.35rem 0.7rem;
  }
  .agreement-section { margin-top: 0.7rem; }
  .agreement-section h2 {
    font-size: 0.85rem;
    margin: 0 0 0.35rem;
    padding-bottom: 0.25rem;
  }
  .clauses { margin: 0; padding-left: 1.2rem; }
  .clauses li { margin-bottom: 0.25rem; }
}
</style>
